import {useAuth as useItsaiAuth, type LoginOptions, type LogoutOptions} from "@it-s-ai/auth";
import {useStores} from "stores/rootStore";
import {clearPersistedAuth} from "persistence";
import {persistLoginState} from "loginDrafts";
import type {PostLoginAction} from "postLoginAction";
import {logEvent} from "analytics";
import {gtmEvent} from "utils/gtm";
import {useCallback} from "react";
import {useMatches} from "react-router-dom";
import {LogoutOfflineModal} from "modals/LogoutOfflineModal";
import {useLocalizedPath} from "i18n/hooks";
import {resultLoginCopy} from './loginCopy';

// Why the sign-up / log-in modal is being opened. Drives the context-aware
// badge / title / subtitle / highlighted perk in @it-s-ai/auth's login view.
// 'chars' is the character-cap wall an anon meets on the counter; the copy it needs
// is passed along with it (see useAuth login / LoginOptions.copy).
// 'purchase' is the guest-checkout claim — it drops the free-plan footer, which
// contradicts the payment that already went through.
// 'humanizer' is the Humanize click of an anon — the copy it needs travels with
// it (see GlobalMechanics.humanizerWallCopy).
export type {SignupReason, LoginCopy} from "@it-s-ai/auth";


export const useAuth = () => {

    const root = useStores()
    const {user: userStore, input} = root

    const {authenticated: itsaiAuth, ready, login, logout: itsaiLogout} = useItsaiAuth();

    const logout_ = useCallback(async (options?: LogoutOptions) => {
        // Contentsquare reset identity - https://docs.contentsquare.com/en/web/track-users/
        (window as any)._uxa = (window as any)._uxa || [];
        (window as any)._uxa.push(["resetIdentity"]);

        userStore.setLoggingOut()
        userStore.logout()
        clearPersistedAuth();
        itsaiLogout(options)
    }, [userStore, itsaiLogout]);

    // Options (prefillEmail / forceMode / persistent) are forwarded to the auth
    // library so a post-payment guest claim can open the modal pre-filled and
    // resist accidental dismissal.
    const login_ = useCallback((options?: LoginOptions & {
        continueScan?: boolean;
        resume?: (() => void) | null;
        action?: PostLoginAction | null;
    }) => {
        // continueScan is set only when the login was triggered to continue a scan
        // (the limit auth form). It rides along with the draft so the post-login
        // restore re-runs the scan; a voluntary login persists the draft with
        // autoRun=false and is only restored, not re-run.
        // `resume` and `action` are ours, not the lib's — everything else is forwarded as it comes.
        const {resume = null, action = null, ...libOptions} = options ?? {}
        if (libOptions.reason === 'results' && !libOptions.copy) {
            libOptions.copy = resultLoginCopy('ai');
        }
        const autoRun = Boolean(libOptions.continueScan)
        libOptions.analyticsContext = {
            ...(autoRun || (libOptions.reason && libOptions.reason !== 'general') ? input.measurement?.() : {}),
            signup_entrypoint: autoRun ? 'continue_scan' : action?.type ?? libOptions.reason ?? 'general',
            ...(autoRun ? {paywall_trigger: input.loginPromptTrigger} : {}),
            ...libOptions.analyticsContext,
        }
        // Drafts and the continuation are armed HERE, by the login itself, so one whose prompt
        // the user closed cannot ride along with a later, unrelated login.
        persistLoginState(root, {autoRun, humanizerRun: libOptions.reason === 'humanizer', action, resume})
        login(libOptions)
    }, [login, root])

    return {
        logout: logout_,
        login: login_,
        ready: ready,
        initialized: userStore.initialized,
        authenticated: userStore.isAuthenticated(),
        isAnon: userStore.isAnonymous,
    };
}


/** Route `handle` of the account-only pages, the ones rendered inside AuthGuard. */
export const AUTH_ONLY = {authOnly: true};
/** Where an account-only page sends whoever has no account: on logout, or on a closed login. */
export const AUTH_ONLY_EXIT = '/main/scan';

export const useMyLogout = () => {
    const {logout} = useAuth();
    const root = useStores();
    const onAuthOnlyPage = useMatches().some(match => (match.handle as Partial<typeof AUTH_ONLY> | undefined)?.authOnly);
    const localizedPath = useLocalizedPath();
    return async () => {
        // Logging out ends in a page load, which has nothing to load offline: stay signed in and say why.
        if (!navigator.onLine) {
            root.modal.setModal(<LogoutOfflineModal/>);
            return;
        }
        try {
            await logEvent(gtmEvent("GTM_user_logout", {page_type: "app_page", entrypoint: "account_menu"}))
        } catch (e) {}
        // An account-only page would reload straight into the login wall.
        await logout(onAuthOnlyPage ? {redirectTo: localizedPath(AUTH_ONLY_EXIT)} : undefined);
    }
}
